import { ArrowForward } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { blue } from '@mui/material/colors';
import { useEntityActions } from 'api/entity';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useModal } from 'components/modal';
import { useNotifications } from 'components/notify';
import { FinsightEntityFieldValue } from 'components/renderField';
import { motion } from 'framer-motion';
import { useDraggable } from 'providers/DnDProvider';
import React, { useEffect, useState } from 'react';
import { FinsightEntity } from 'shared';
import { useSWRConfig } from 'swr';
import { getCommonSortableFields, sortFields } from 'utils/fields';

type Action = {
  field: string;
  value: unknown;
};

export default function DroppableList({
  context,
}: {
  context: {
    entitiesList: FinsightEntity[];
    entityBefore?: FinsightEntity;
    entityAfter?: FinsightEntity;
  };
}) {
  const dragContext = useDraggable();

  const { showModal } = useModal();

  const [dropMode, setDropMode] = useState(false);

  useEffect(() => {
    if (!dragContext.dragItem) {
      setDropMode(false);
    }
  }, [dragContext.dragItem]);

  return (
    <motion.div
      initial={{
        height: 0,
      }}
      animate={{
        height: dropMode ? 16 : 8,
      }}
      onMouseEnter={() => {
        if (dragContext.dragItem && dragContext.dragItem.type === 'entity') {
          setDropMode(true);
        }
      }}
      onMouseLeave={() => {
        setDropMode(false);
      }}
      onMouseUp={() => {
        if (!dragContext.dragItem || dragContext.dragItem.type !== 'entity')
          return;
        const targetEntity = dragContext.dragItem.data as FinsightEntity;

        if (
          context.entityBefore?.id === targetEntity.id ||
          context.entityAfter?.id === targetEntity.id
        )
          return;
        const entities: FinsightEntity[] = [targetEntity];
        if (context.entityBefore) entities.push(context.entityBefore);
        if (context.entityAfter) entities.push(context.entityAfter);

        const _commonFields = getCommonSortableFields(entities);

        let commonFields: string[] = [];

        for (const field of _commonFields) {
          if (
            targetEntity.extraFieldsMeta[field]?.schema.type !== 'computed' &&
            context.entityBefore?.extraFieldsMeta[field]?.schema.type !==
              'computed' &&
            context.entityAfter?.extraFieldsMeta[field]?.schema.type !==
              'computed'
          ) {
            commonFields.push(field);
          }
        }

        commonFields = sortFields(
          commonFields.map((x) => targetEntity.extraFieldsMeta[x]),
        ).map((x) => x.name);

        let highestIndex = -1;

        const entityIndex = context.entitiesList.findIndex(
          (x) => x.id === targetEntity.id,
        );
        const beforeIndex = context.entityBefore
          ? context.entitiesList.findIndex(
              (x) => x.id === context.entityBefore?.id,
            )
          : -1;
        const afterIndex = context.entityAfter
          ? context.entitiesList.findIndex(
              (x) => x.id === context.entityAfter?.id,
            )
          : -1;

        const fromAbove = entityIndex < beforeIndex || entityIndex < afterIndex;

        for (let i = 0; i < commonFields.length; i++) {
          const field = commonFields[i];

          const targetValue = targetEntity.extraFields[field];
          const beforeValue = context.entityBefore?.extraFields[field];
          const afterValue = context.entityAfter?.extraFields[field];

          if (context.entityBefore) {
            if (targetValue !== beforeValue) {
              highestIndex = i;
              break;
            }
          }

          if (context.entityAfter) {
            if (targetValue !== afterValue) {
              highestIndex = i;
              break;
            }
          }
        }

        const controllingFields = commonFields.slice(highestIndex);

        let actions: Action[] = [];

        for (let i = 0; i < controllingFields.length; i++) {
          const field = controllingFields[i];
          // const targetValue = targetEntity.extraFields[field];
          const beforeValue = context.entityBefore?.extraFields[field];
          const afterValue = context.entityAfter?.extraFields[field];

          let updated = false;

          const orderOfCompute: ('before' | 'after')[] = [];

          if (fromAbove) {
            orderOfCompute.push('before');
            orderOfCompute.push('after');
          } else {
            orderOfCompute.push('after');
            orderOfCompute.push('before');
          }

          for (const order of orderOfCompute) {
            const currentEntity =
              order === 'before' ? context.entityBefore : context.entityAfter;

            const currentValue = order === 'before' ? beforeValue : afterValue;

            if (currentEntity && !updated) {
              if (i === controllingFields.length - 1) {
                const currentValueNum = Number(currentValue);
                actions.push({
                  field,
                  value: isNaN(currentValueNum)
                    ? currentValue
                    : order === 'before'
                      ? currentValueNum - 1
                      : currentValueNum + 1,
                });
              } else {
                actions.push({
                  field,
                  value: currentValue,
                });
              }
              // if (targetValue !== currentValue) {
              //   if (i === controllingFields.length - 1) {
              //     console.log(
              //       'update',
              //       field,
              //       'to',
              //       currentValue,
              //       order === 'before' ? 'minus one' : 'plus one',
              //     );
              //   } else {
              //     console.log('update', field, 'to', currentValue);
              //   }
              // } else {
              //   console.log(
              //     'update',
              //     field,
              //     'to',
              //     currentValue,
              //     order === 'before' ? 'minus one' : 'plus one',
              //   );
              // }
              updated = true;
            }
          }
        }

        actions = actions.filter(
          (x) => targetEntity.extraFields[x.field] !== x.value,
        );

        if (actions.length) {
          showModal(
            'Quick Update Entity',
            <DragFieldUpdateModal entity={targetEntity} actions={actions} />,
          );
        }
      }}
    >
      <motion.div
        initial={{
          height: 0,
        }}
        animate={{
          height: dropMode ? '100%' : 0,
        }}
        style={{
          width: '100%',
          display: 'flex',
          overflow: 'hidden',
          alignItems: 'center',
        }}
      >
        <motion.div
          style={{
            width: 4,
            height: '100%',
            borderRadius: '100px',
            backgroundColor: blue[700],
          }}
        />

        <motion.div
          style={{
            height: 4,
            flexGrow: 1,
            backgroundColor: blue[700],
          }}
        />
        <motion.div
          style={{
            width: 4,
            height: '100%',
            borderRadius: '100px',
            backgroundColor: blue[700],
          }}
        />
      </motion.div>
    </motion.div>
  );
}

export function DragFieldUpdateModal(props: {
  entity: FinsightEntity;
  actions: Action[];
  defaultSelectAll?: boolean;
  allOptional?: boolean;
  onUpdated?: () => void;
  closeModal?: () => void;
}) {
  const {
    actions: originalActions,
    entity,
    defaultSelectAll,
    allOptional,
    closeModal,
  } = props;

  const [actions, setActions] = useState<
    Array<
      Action & {
        selected?: boolean;
      }
    >
  >(
    originalActions.map((x) => ({
      ...x,
      selected: defaultSelectAll ? true : undefined,
    })),
  );

  const [updating, setUpdating] = useState(false);

  const { showNotification } = useNotifications();

  const { updateEntity } = useEntityActions();

  const { cache, mutate } = useSWRConfig();
  const workspaceId = useWorkspaceId();

  return (
    <>
      <Box display="grid" gridTemplateColumns="1fr 64px 1fr">
        {actions.map((action, index) => (
          <React.Fragment key={action.field}>
            {index !== 0 && (
              <Divider
                sx={{
                  gridColumn: '1/4',
                  my: 3,
                }}
              />
            )}
            <Stack
              pb={action.selected || index === 0 ? 3 : 0}
              gridColumn="1/4"
              direction="row"
              alignItems="center"
              gap={2}
            >
              {(allOptional ? true : index !== 0) && (
                <Checkbox
                  disabled={updating}
                  checked={!!action.selected}
                  sx={{ p: 0 }}
                  onClick={() =>
                    setActions((prev) => {
                      const newActions = [...prev];

                      if (!newActions[index].selected) {
                        newActions[index].selected = true;
                      } else {
                        newActions[index] = {
                          ...originalActions[index],
                          selected: false,
                        };
                      }

                      return newActions;
                    })
                  }
                />
              )}
              <Typography
                textTransform="uppercase"
                letterSpacing={2}
                fontSize="1.1em"
              >
                {action.field}
              </Typography>
            </Stack>
            {(!!action.selected || (allOptional ? false : index === 0)) && (
              <>
                <Stack gridColumn={1} sx={{ cursor: 'not-allowed' }}>
                  <Stack
                    sx={{
                      p: 1,
                      justifyContent: 'center',
                      alignItems: 'stretch',
                      aspectRatio: '7/4',
                      borderRadius: 2,
                      backgroundColor: '#0f0f0f',
                      flex: '1 1 50%',
                      pointerEvents: 'none',
                    }}
                  >
                    {action.field === 'name' ||
                    action.field === 'description' ? (
                      <div />
                    ) : (
                      <FinsightEntityFieldValue
                        editable
                        field={entity.extraFieldsMeta[action.field]}
                        value={entity.extraFields[action.field]}
                        viewType={'any' as any}
                        options={{
                          disableConfigBadge: true,
                        }}
                      />
                    )}
                  </Stack>
                </Stack>
                <Box
                  gridColumn={2}
                  alignItems="center"
                  justifyContent="center"
                  display="flex"
                >
                  <ArrowForward />
                </Box>
                <Stack gridColumn={3}>
                  <Stack
                    sx={{
                      p: 1,
                      justifyContent: 'center',
                      alignItems: 'stretch',
                      aspectRatio: '7/4',
                      flex: '1 1 50%',
                      borderRadius: 2,
                      minWidth: 200,
                      backgroundColor: '#0f0f0f',
                    }}
                  >
                    {action.field === 'name' ||
                    action.field === 'description' ? (
                      <Typography>{`${action.value ?? ''}`}</Typography>
                    ) : (
                      <FinsightEntityFieldValue
                        editable
                        field={entity.extraFieldsMeta[action.field]}
                        value={action.value}
                        viewType={'any' as any}
                        options={{
                          disableConfigBadge: true,
                        }}
                        onChange={async (value) =>
                          setActions((prev) => {
                            const newActions = [...prev];
                            newActions[index] = {
                              ...newActions[index],
                              value,
                            };
                            return newActions;
                          })
                        }
                      />
                    )}
                  </Stack>
                </Stack>
              </>
            )}
          </React.Fragment>
        ))}
      </Box>
      <Button
        sx={{
          mt: 2,
        }}
        disabled={updating}
        onClick={async () => {
          let name = null;
          let description = null;
          const fieldsToUpdate: Record<string, unknown> = {};

          for (const i in actions) {
            const action = actions[i];
            if (action.selected || (allOptional ? false : i === '0')) {
              if (action.field === 'name') {
                name = action.value;
              } else if (action.field === 'description') {
                description = action.value;
              } else {
                fieldsToUpdate[action.field] = action.value;
              }
            }
          }

          if (
            !name &&
            !description &&
            Object.keys(fieldsToUpdate).length === 0
          ) {
            props.onUpdated?.();
            return;
          }

          setUpdating(true);
          showNotification('Updating entity...', {
            loading: true,
          });

          try {
            await updateEntity(entity.id, {
              ...(typeof name === 'string' ? { name } : {}),
              ...(typeof description === 'string' ? { description } : {}),
              extraFields: fieldsToUpdate,
            });

            showNotification('Entity updated successfully', {
              variant: 'success',
            });

            if (entity.parent) {
              Array.from(cache.keys())
                .filter((x) =>
                  x.includes(
                    `/workspace/${workspaceId}/entity/${entity.parent}`,
                  ),
                )
                .forEach((key) => mutate(key));
            } else {
              Array.from(cache.keys())
                .filter((x) =>
                  x.includes(
                    `/workspace/${workspaceId}/entities/${entity.types[0]}/root`,
                  ),
                )
                .forEach((key) => mutate(key));
            }

            Array.from(cache.keys())
              .filter((x) =>
                x.includes(
                  `/workspace/${workspaceId}/datamaid/history/field-autofill`,
                ),
              )
              .forEach((key) => mutate(key));

            closeModal?.();
            props.onUpdated?.();
          } catch (e) {
            const message =
              e instanceof Error ? e.message : 'An error occurred';
            showNotification(message, {
              variant: 'error',
            });
          } finally {
            setUpdating(false);
          }
        }}
      >
        Update Entity
      </Button>
    </>
  );
}

export type DragFieldUpdateModalAction = Action;
